// BEP-1821: Read-only holder lookup trail component.
// Renders backend-provided episodes verbatim — never derives EXPIRED or any outcome itself.
import React from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import Style from "./holderLookupTrail.module.css";

// ---------------------------------------------------------------------------
// eventKind → { color, labelKey }
// color is one of: "neutral" | "blue" | "green" | "amber" | "red"
// Unknown kinds fall back to the generic renderer (forward-compat rule).
// ---------------------------------------------------------------------------
const EVENT_KIND_MAP = {
  VA_CREATED:             { color: "neutral", labelKey: "ViolatorsAddressModal.Trail.event.VA_CREATED" },
  TASK_IN_PROGRESS:       { color: "blue",    labelKey: "ViolatorsAddressModal.Trail.event.TASK_IN_PROGRESS" },
  TASK_FAILED:            { color: "red",     labelKey: "ViolatorsAddressModal.Trail.event.TASK_FAILED" },
  VA_REUSE_PENDING:       { color: "neutral", labelKey: "ViolatorsAddressModal.Trail.event.VA_REUSE_PENDING" },
  VA_DETERMINED:          { color: "green",   labelKey: "ViolatorsAddressModal.Trail.event.VA_DETERMINED" },
  VA_DETERMINED_REUSED:   { color: "green",   labelKey: "ViolatorsAddressModal.Trail.event.VA_DETERMINED_REUSED" },
  VA_UNDETERMINED:        { color: "neutral", labelKey: "ViolatorsAddressModal.Trail.event.VA_UNDETERMINED" },
  VA_FOREIGN:             { color: "neutral", labelKey: "ViolatorsAddressModal.Trail.event.VA_FOREIGN" },
  VA_CANCELLED:           { color: "neutral", labelKey: "ViolatorsAddressModal.Trail.event.VA_CANCELLED" },
  KBA_BUILT:              { color: "neutral", labelKey: "ViolatorsAddressModal.Trail.event.KBA_BUILT" },
  KBA_SENT:               { color: "blue",    labelKey: "ViolatorsAddressModal.Trail.event.KBA_SENT" },
  KBA_RESPONSE_IMPORTED:  { color: "blue",    labelKey: "ViolatorsAddressModal.Trail.event.KBA_RESPONSE_IMPORTED" },
  KBA_LATE_RESPONSE:      { color: "red",     labelKey: "ViolatorsAddressModal.Trail.event.KBA_LATE_RESPONSE" },
  EXPIRED:                { color: "amber",   labelKey: "ViolatorsAddressModal.Trail.event.EXPIRED" },
  // INPUT_CHANGED is rendered as a divider, handled separately in episode list
};

// Maps color name to CSS module classes
const DOT_CLASS    = { neutral: Style.dotNeutral, blue: Style.dotBlue, green: Style.dotGreen, amber: Style.dotAmber, red: Style.dotRed };
const LABEL_CLASS  = { neutral: Style.eventLabelNeutral, blue: Style.eventLabelBlue, green: Style.eventLabelGreen, amber: Style.eventLabelAmber, red: Style.eventLabelRed };

// Maps currentOutcome → badge color
function outcomeBadgeColor(outcome) {
  if (!outcome) return "neutral";
  if (outcome === "DETERMINED" || outcome === "DETERMINED_REUSED") return "green";
  if (outcome === "LOOKUP_IN_PROGRESS" || outcome === "REUSE_PENDING") return "blue";
  if (outcome === "LOOKUP_PENDING") return "blue";
  if (outcome === "LOOKUP_CANCELLED") return "neutral";
  if (outcome === "UNDETERMINED" || outcome === "FOREIGN") return "neutral";
  return "neutral";
}

const BADGE_CLASS = { green: Style.badgeGreen, blue: Style.badgeBlue, amber: Style.badgeAmber, red: Style.badgeRed, neutral: Style.badgeNeutral };

function formatTs(iso) {
  return iso ? moment(iso).format("DD.MM. HH:mm") : null;
}

// "waiting since N days" — only shown on the currently waiting/in-progress event
function relativeHint(iso) {
  if (!iso) return null;
  const diff = moment().diff(moment(iso), "days");
  if (diff < 1) return null;
  return diff;
}

// ---------------------------------------------------------------------------
// TimelineEvent
// ---------------------------------------------------------------------------
function TimelineEvent({ event, isLast, episodeFinished, t }) {
  const kind = event.eventKind;
  const mapping = EVENT_KIND_MAP[kind];
  // TASK_IN_PROGRESS is blue only when it is the last event AND the episode is still open —
  // i.e. it is actually happening now. In a finished episode, or when superseded by later
  // events, it reads as historical and uses neutral to avoid implying ongoing activity.
  const color = (mapping && !(kind === "TASK_IN_PROGRESS" && (!isLast || episodeFinished)))
    ? mapping.color
    : "neutral";
  const dotClass = DOT_CLASS[color];
  const labelClass = LABEL_CLASS[color];

  // Label: known kind → translated, unknown → generic from status/explanation
  let label;
  if (mapping) {
    label = t(mapping.labelKey);
  } else {
    label = event.status || kind || t("ViolatorsAddressModal.Trail.event.unknown");
  }

  const showStatusDetail = kind === "KBA_LATE_RESPONSE" && event.status;
  // Show relative hint only on the last TASK_IN_PROGRESS event
  const isWaiting = kind === "TASK_IN_PROGRESS" && isLast;

  return (
    <div className={Style.timelineEvent}>
      <span className={`${Style.timelineDot} ${dotClass}`} />
      <div className={Style.eventTime}>{formatTs(event.occurredAt)}</div>
      <div className={`${Style.eventLabel} ${labelClass}`}>{label}</div>
      {showStatusDetail && (
        <div className={Style.eventExplanation}>{event.status}</div>
      )}
      {event.explanation && (
        <div className={Style.eventExplanation}>{event.explanation}</div>
      )}
      {isWaiting && (() => {
        const days = relativeHint(event.occurredAt);
        return days
          ? <div className={Style.eventRelativeHint}>{t("ViolatorsAddressModal.Trail.waitingSince", { count: days })}</div>
          : null;
      })()}
    </div>
  );
}

// ---------------------------------------------------------------------------
// EpisodeCard
// ---------------------------------------------------------------------------
function EpisodeCard({ episode, index, t }) {
  const { licensePlate, licensePlateCountryCode, vehicleType, provider,
          startedAt, finishedAt, currentOutcome, events } = episode;
  const episodeFinished = finishedAt != null;

  const hasPlate = licensePlate != null;
  const showVehicleType = vehicleType && vehicleType !== "Car";

  // Outcome badge label: reuse existing Statuses keys where possible
  const badgeKey = currentOutcome
    ? `ViolatorsAddressModal.Statuses.${currentOutcome.toLowerCase().replace(/_/g, "_")}`
    : null;
  const badgeLabel = badgeKey
    ? t(badgeKey, { defaultValue: currentOutcome })
    : t("ViolatorsAddressModal.Trail.outcomeUnknown");
  const badgeColor = outcomeBadgeColor(currentOutcome);

  // Timing line
  const start = formatTs(startedAt);
  const end   = formatTs(finishedAt);
  const timingStr = start
    ? end ? `${start} → ${end}` : `${start} →`
    : null;

  // Separate INPUT_CHANGED events (they become inter-episode dividers, not timeline events)
  const timelineEvents = events.filter(e => e.eventKind !== "INPUT_CHANGED");

  return (
    <div className={Style.episodeCard}>
      <div className={Style.episodeHeader}>
        <div className={Style.episodeMeta}>
          <span className={Style.episodeTitle}>
            {t("ViolatorsAddressModal.Trail.lookup", { n: index + 1 })}
            {hasPlate && (
              <span className={Style.episodePlate}>
                {" "}·{" "}
                {licensePlateCountryCode && <>{licensePlateCountryCode} · </>}
                {licensePlate}
                {showVehicleType && (
                  <> · {t(`Violations.VehicleType.${vehicleType}`, { defaultValue: vehicleType })}</>
                )}
                {provider && <span style={{ color: "#888" }}> · {provider}</span>}
              </span>
            )}
            {!hasPlate && (
              <span className={Style.episodePlateUnknown}>
                {" "}· {t("ViolatorsAddressModal.Trail.plateUnknown")}
              </span>
            )}
          </span>
        </div>
        {currentOutcome && (
          <span className={`${Style.badge} ${BADGE_CLASS[badgeColor]}`}>
            {badgeLabel}
          </span>
        )}
      </div>

      {timingStr && <div className={Style.episodeTiming}>{timingStr}</div>}

      {timelineEvents.length > 0 && (
        <div className={Style.timeline}>
          {timelineEvents.map((ev, i) => (
            <TimelineEvent
              key={`${ev.sourceEntity}-${ev.sourceId}-${ev.sourceRevision ?? "derived"}-${i}`}
              event={ev}
              isLast={i === timelineEvents.length - 1}
              episodeFinished={episodeFinished}
              t={t}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// InputChangedDivider
// ---------------------------------------------------------------------------
function InputChangedDivider({ event, t }) {
  const when = formatTs(event.occurredAt);
  const detail = event.explanation || "";
  return (
    <div className={Style.inputChangedDivider}>
      ✎ {t("ViolatorsAddressModal.Trail.inputChanged")}
      {detail && ` — ${detail}`}
      {when && ` · ${when}`}
    </div>
  );
}

// ---------------------------------------------------------------------------
// HolderLookupTrail (main export)
// ---------------------------------------------------------------------------
const HolderLookupTrail = ({ episodes, loading }) => {
  const { t } = useTranslation();

  return (
    <div className={Style.trailSection}>
      <div className={Style.trailHeading}>
        {t("ViolatorsAddressModal.Trail.heading")}
      </div>

      {loading && (
        <div>
          <div className={Style.loadingText}>{t("ViolatorsAddressModal.Trail.loading")}</div>
          <div className={Style.skeletonLine} style={{ width: "80%" }} />
          <div className={Style.skeletonLine} style={{ width: "60%" }} />
          <div className={Style.skeletonLine} style={{ width: "70%" }} />
        </div>
      )}

      {!loading && (
        <div className={Style.trailList}>
          {episodes.map((episode, episodeIdx) => {
            // Collect INPUT_CHANGED events from the NEXT episode's event list that
            // should appear as dividers before this episode (index > 0).
            // The backend interleaves INPUT_CHANGED into adjacent episode event lists.
            // We surface any INPUT_CHANGED from the current episode's events as a
            // trailing divider after this card (i.e., before the next episode).
            const inputChangedEvents = episode.events.filter(e => e.eventKind === "INPUT_CHANGED");

            return (
              <React.Fragment key={episode.violatorAddressId}>
                <EpisodeCard episode={episode} index={episodeIdx} t={t} />
                {inputChangedEvents.map((ev, i) => (
                  <InputChangedDivider
                    key={`ic-${episode.violatorAddressId}-${i}`}
                    event={ev}
                    t={t}
                  />
                ))}
              </React.Fragment>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default HolderLookupTrail;
